/**
 * The add-on catalogue table (TMMPDM-0019): Name (+ kind) · Period · Price · Billing · Status ·
 * Actions. Row actions follow the item's status: DRAFT → Edit…, Activate; ACTIVE → Edit…, Retire;
 * RETIRED → Reinstate. Lifecycle actions PATCH `{status}` only, after a confirm.
 */
import { useState } from 'react';
import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, EmptyState, RowActionsMenu, hasEditableFields } from '@/components/page';
import { Amount } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy } from '@/api/errors';
import type { CatalogView } from '@/api/tmm/types';
import { ALLOWED_BILLING_FALLBACK, CATALOG_STATUS_FALLBACK, PERIOD_FALLBACK } from './addonFormat';
import { StatusChip } from '@/status';

interface CatalogTableProps {
  items: CatalogView[];
  loading: boolean;
  locale: string;
  onEdit: (item: CatalogView) => void;
  onEditDetails: (item: CatalogView) => void;
  onChanged: (item: CatalogView, kind: 'activated' | 'retired') => void;
}

export function CatalogTable({
  items,
  loading,
  locale,
  onEdit,
  onEditDetails,
  onChanged,
}: CatalogTableProps) {
  const { t } = useCopy();
  const [confirm, setConfirm] = useState<{ item: CatalogView; to: 'ACTIVE' | 'RETIRED' } | null>(
    null,
  );
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  // A failed change is said inside its confirm, which stays open (TMM-B4).
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const openConfirm = (item: CatalogView, to: 'ACTIVE' | 'RETIRED') => {
    setConfirmError(null);
    setConfirmName(item.name);
    setConfirm({ item, to });
  };

  const transition = async (item: CatalogView, to: 'ACTIVE' | 'RETIRED') => {
    setBusy(true);
    setConfirmError(null);
    try {
      const next = await adminApi.patchAddonCatalog(item.kind, { status: to });
      setConfirm(null);
      onChanged(next, to === 'ACTIVE' ? 'activated' : 'retired');
    } catch (err: unknown) {
      setConfirmError(errorCopy(err, t, t('addons.saveError', 'Could not save the add-on.')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('addons.catalog.tableAria', 'Add-on catalogue')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('addons.catalog.col.name', 'Name')}</TableCell>
              <TableCell>{t('addons.catalog.col.period', 'Period')}</TableCell>
              <TableCell>{t('addons.catalog.col.price', 'Price')}</TableCell>
              <TableCell>{t('addons.catalog.col.billing', 'Billing')}</TableCell>
              <TableCell>{t('addons.catalog.col.status', 'Status')}</TableCell>
              <TableCell>{t('addons.catalog.col.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('common.loading', 'Loading')}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <EmptyState title={t('addons.catalog.empty', 'Nothing in the catalogue yet.')} />
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.kind}>
                  <TableCell>
                    <Typography variant="body2">{item.name}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={(theme) => theme.typography.mono}
                    >
                      {item.kind}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {t(`addons.period.${item.period}`, PERIOD_FALLBACK[item.period])}
                  </TableCell>
                  <TableCell>
                    <Amount
                      minor={item.defaultPriceMinor}
                      currency={item.currency}
                      locale={locale}
                    />
                  </TableCell>
                  <TableCell>
                    {t(
                      `addons.allowedBilling.${item.allowedBilling}`,
                      ALLOWED_BILLING_FALLBACK[item.allowedBilling],
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      status={item.status}
                      label={t(
                        `addons.catalogStatus.${item.status}`,
                        CATALOG_STATUS_FALLBACK[item.status],
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      {item.status === 'RETIRED' ? null : (
                        <Button size="small" onClick={() => onEdit(item)} disabled={busy}>
                          {t('addons.actions.edit', 'Edit…')}
                        </Button>
                      )}
                      <RowActionsMenu
                        name={item.name}
                        items={[
                          ...(hasEditableFields(
                            item.editableFields?.filter((field) => field !== 'status'),
                          )
                            ? [
                                {
                                  key: 'details',
                                  label: t('components.editDetails.menu', 'Edit details…'),
                                  onSelect: () => onEditDetails(item),
                                },
                              ]
                            : []),
                          ...(item.status === 'DRAFT'
                            ? [
                                {
                                  key: 'activate',
                                  label: t('addons.actions.activate', 'Activate'),
                                  onSelect: () => openConfirm(item, 'ACTIVE'),
                                },
                              ]
                            : []),
                          ...(item.status === 'ACTIVE'
                            ? [
                                {
                                  key: 'retire',
                                  label: t('addons.actions.retire', 'Retire'),
                                  destructive: true,
                                  onSelect: () => openConfirm(item, 'RETIRED'),
                                },
                              ]
                            : []),
                          ...(item.status === 'RETIRED'
                            ? [
                                {
                                  key: 'reinstate',
                                  label: t('addons.actions.reinstate', 'Reinstate…'),
                                  onSelect: () => openConfirm(item, 'ACTIVE'),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={confirm?.to === 'ACTIVE'}
        title={
          confirm?.item.status === 'RETIRED'
            ? t('addons.reinstate.title', 'Reinstate {{name}}?', { name: confirmName })
            : t('addons.activate.title', 'Activate {{name}}?', { name: confirmName })
        }
        consequence={
          confirm?.item.status === 'RETIRED'
            ? t('addons.reinstate.confirm', 'It can be offered to tenants again.')
            : t('addons.activate.confirm', 'Tenants can then be offered it.')
        }
        confirmLabel={
          confirm?.item.status === 'RETIRED'
            ? t('addons.reinstate.action', 'Reinstate')
            : t('addons.actions.activate', 'Activate')
        }
        busy={busy}
        error={confirmError}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm) void transition(confirm.item, 'ACTIVE');
        }}
      />
      <ConfirmDialog
        open={confirm?.to === 'RETIRED'}
        title={t('addons.retire.title', 'Retire {{name}}?', { name: confirmName })}
        consequence={t(
          'addons.retire.confirm',
          "It can't be offered any more. Tenants who have it keep it.",
        )}
        confirmLabel={t('addons.actions.retire', 'Retire')}
        destructive
        busy={busy}
        error={confirmError}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm) void transition(confirm.item, 'RETIRED');
        }}
      />
    </>
  );
}
